/**
 * Auth queries + mutations.
 *
 *   POST /api/auth/login   → { user, csrfToken } + session cookie set
 *   POST /api/auth/logout  → clears session
 *   GET  /api/auth/me      → { user, csrfToken } (CSRF token rehydrated)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest, setCsrfToken } from './api-client';

export interface MeUser {
  id: string;
  username: string;
  displayName: string;
  email: string;
  role: 'ADMIN' | 'ASSISTANT';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
}

export interface MeResponse {
  user: MeUser;
  /** CSRF token returned from /login and /me. The api-client stores it in
   *  memory and sends it on mutating requests via the X-CSRF-Token header. */
  csrfToken: string;
}

export const currentUserQueryKey = ['auth', 'me'] as const;

export function useCurrentUser() {
  return useQuery({
    queryKey: currentUserQueryKey,
    queryFn: async ({ signal }) => {
      try {
        const me = await apiRequest<MeResponse>('/api/auth/me', { signal });
        // Restore the CSRF token after a page refresh — the session cookie
        // persists across reloads but in-memory state doesn't, so /me
        // re-emits the HMAC of the active session.
        setCsrfToken(me.csrfToken);
        return me;
      } catch (err) {
        if (err instanceof ApiError && err.isUnauthorized()) {
          setCsrfToken(null);
          return null;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<null>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(currentUserQueryKey, null);
      qc.clear();
      // Clear the CSRF token from memory; any further mutation must establish
      // a new session before it can succeed.
      setCsrfToken(null);
    },
  });
}

// ---------------------------------------------------------------------------
// Login — single POST that mints the session and returns the user + CSRF token.
// ---------------------------------------------------------------------------

export interface LoginRequest {
  username: string;
  password: string;
}

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiError, LoginRequest>({
    mutationFn: (body) => apiRequest<MeResponse>('/api/auth/login', { method: 'POST', body }),
    onSuccess: (data) => {
      qc.setQueryData(currentUserQueryKey, data);
      // Capture CSRF token before the next mutation goes out.
      setCsrfToken(data.csrfToken);
    },
  });
}
